import { ConsolaInstance, createConsola } from 'consola';

import { LogMetadata, Loggable, isDebugEnabled } from './base';

type Options = {
  metadata?: LogMetadata;
  service?: string;
};
/**
 * Local Loki logger implementation
 * This should only be used during local development and will push logs directly to the
 * loki instance running in Docker
 */
export class LocalLokiLogger implements Loggable {
  private consoleLogger: ConsolaInstance;
  private commonMetadata: LogMetadata;
  private lokiUrl: string;
  private readonly service: string;

  constructor(prefix: string, options: Options = {}) {
    this.consoleLogger = createConsola();
    const metadata = options?.metadata ?? {};
    this.service = options?.service ?? 'web-vite';
    this.commonMetadata = { context: prefix, ...metadata };
    this.lokiUrl = 'https://loki.eluve.dev/loki/api/v1/push';
  }

  info = (message: string, logMetadata?: LogMetadata) => {
    const metadata = { ...this.commonMetadata, ...logMetadata };
    if (isDebugEnabled()) {
      this.consoleLogger.info(this.getDateString(), message, metadata);
    }
    this.pushToLoki('info', message, metadata);
  };

  warn = (message: string, logMetadata?: LogMetadata) => {
    const metadata = { ...this.commonMetadata, ...logMetadata };
    if (isDebugEnabled()) {
      this.consoleLogger.warn(this.getDateString(), message, metadata);
    }
    this.pushToLoki('warn', message, metadata);
  };

  error = (message: string, logMetadata?: LogMetadata) => {
    const metadata = { ...this.commonMetadata, ...logMetadata };
    this.consoleLogger.error(this.getDateString(), message, metadata);
    this.pushToLoki('error', message, metadata);
  };

  debug = (message: string, logMetadata?: LogMetadata) => {
    const metadata = { ...this.commonMetadata, ...logMetadata };
    this.consoleLogger.debug(this.getDateString(), message, metadata);
    this.pushToLoki('debug', message, metadata);
  };

  private getDateString = () => `[${new Date().toLocaleString()}]`;

  /**
   * Push log entry to Loki
   * Sends logs asynchronously to local Loki instance without blocking
   */
  private pushToLoki = (
    level: 'info' | 'warn' | 'error' | 'debug',
    message: string,
    metadata: LogMetadata,
  ) => {
    // Don't block on Loki push - fire and forget
    const timestamp = Date.now();
    const nanoseconds = `${timestamp}000000`; // Convert ms to ns

    // Format log line as JSON with message and metadata
    const logLine = JSON.stringify({
      message,
      level,
      timestamp: new Date(timestamp).toISOString(),
      ...metadata,
    });

    const payload = {
      streams: [
        {
          stream: {
            service: this.service,
            type: 'app',
            level,
            context: metadata['context'] ?? 'unknown',
          },
          values: [[nanoseconds, logLine]],
        },
      ],
    };

    fetch(this.lokiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }).catch((error) => {
      // Silently fail - don't spam console if Loki is unavailable
      if (isDebugEnabled()) {
        // eslint-disable-next-line no-console
        console.warn('Failed to push log to Loki:', error);
      }
    });
  };
}
